/**
 * Reusable sx prop presets for consistent styling across the application.
 * Import these presets to reduce inline style duplication.
 */

import { SxProps, Theme } from '@mui/material';
import { BG_COLORS, BORDER_COLORS, TEXT_COLORS, SCROLLBAR_COLORS } from '../utils/colors';
import { BUTTON_SIZES } from '../utils/responsive';

// ============================================
// ACTION BUTTONS (шапка админ-страницы)
// ============================================

/**
 * Кнопка действия админ-страницы («Добавить товар», «Создать промо-код»).
 *
 * У `GlassButton` зашит `padding: 6px 24px`, и на телефоне русская подпись переносилась на
 * вторую строку — кнопка вырастала вдвое и выглядела чужеродно рядом с компактными карточками.
 * Фиксируем высоту из `BUTTON_SIZES` (36/42) и запрещаем перенос; длинные подписи влезают,
 * потому что на xs кнопка занимает всю ширину (см. `adminActionRow`).
 */
export const adminActionButton: SxProps<Theme> = {
  height: BUTTON_SIZES.height,
  minHeight: 0,
  py: 0,
  px: { xs: 1.5, sm: 2 },
  fontSize: BUTTON_SIZES.fontSize,
  whiteSpace: 'nowrap',
  flexShrink: 0,
  '& .MuiSvgIcon-root': { fontSize: { xs: '1rem', sm: '1.25rem' } },
};

/**
 * Ряд кнопок действий: на телефоне в колонку во всю ширину (как итоговая панель `CartPage`
 * в кабинете), от sm — обычной строкой.
 */
export const adminActionRow: SxProps<Theme> = {
  display: 'flex',
  flexDirection: { xs: 'column', sm: 'row' },
  gap: 1,
  mb: { xs: 1.5, sm: 3 },
};

// ============================================
// GLASS SECTION STYLES
// ============================================

/**
 * Glass section/box style - for content sections, info boxes
 * Replaces: sx={{ p: 3, background: 'rgba(255, 255, 255, 0.05)', borderRadius: 2, border: '1px solid rgba(255, 255, 255, 0.1)' }}
 */
export const glassSection: SxProps<Theme> = {
  p: 3,
  background: BG_COLORS.GLASS_DARK,
  borderRadius: 2,
  border: `1px solid ${BORDER_COLORS.DEFAULT}`,
};

/**
 * Compact glass section with smaller padding
 */
export const glassSectionCompact: SxProps<Theme> = {
  p: 2,
  background: BG_COLORS.GLASS,
  borderRadius: 1.5,
  border: `1px solid ${BORDER_COLORS.DEFAULT}`,
};

/**
 * Glass stat box (icon + value layout)
 */
export const glassStatBox: SxProps<Theme> = {
  p: 2,
  background: BG_COLORS.GLASS_DARK,
  borderRadius: 1.5,
  border: `1px solid ${BORDER_COLORS.DEFAULT}`,
  display: 'flex',
  alignItems: 'center',
  gap: 2,
};

// ============================================
// CAPTION / LABEL STYLES
// ============================================

/**
 * Section caption - uppercase label with letter spacing
 * Replaces: sx={{ color: 'rgba(255, 255, 255, 0.5)', textTransform: 'uppercase', letterSpacing: 1, fontWeight: 600, display: 'block' }}
 */
export const sectionCaption: SxProps<Theme> = {
  color: TEXT_COLORS.CAPTION,
  textTransform: 'uppercase',
  letterSpacing: 1,
  fontWeight: 600,
  display: 'block',
};

/**
 * Section caption with margin bottom
 */
export const sectionCaptionMb: SxProps<Theme> = {
  ...sectionCaption,
  mb: 2,
};

// ============================================
// ICON STYLES
// ============================================

/**
 * Circular icon container (48x48)
 */
export const iconCircle: SxProps<Theme> = {
  width: 48,
  height: 48,
  borderRadius: '50%',
  background: BG_COLORS.GLASS_LIGHT,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

/**
 * Small circular icon container (36x36)
 */
export const iconCircleSmall: SxProps<Theme> = {
  width: 36,
  height: 36,
  borderRadius: '50%',
  background: BG_COLORS.GLASS_LIGHT,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

// ============================================
// CHIP STYLES
// ============================================

/**
 * Glass chip style
 * Replaces: sx={{ background: 'rgba(255, 255, 255, 0.1)', border: '1px solid rgba(255, 255, 255, 0.2)', color: 'white', fontWeight: 600 }}
 */
export const glassChip: SxProps<Theme> = {
  background: BG_COLORS.GLASS_LIGHT,
  border: `1px solid ${BORDER_COLORS.STRONG}`,
  color: 'white',
  fontWeight: 600,
  '& .MuiChip-icon': { color: 'white' },
};

/**
 * Rank chip style (for tables)
 */
export const rankChip: SxProps<Theme> = {
  fontWeight: 'bold',
  minWidth: 36,
  height: 28,
  '& .MuiChip-label': {
    px: 1.5,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
};

// ============================================
// BUTTON STYLES
// ============================================

/**
 * Outlined button with glass effect
 */
export const glassOutlinedButton: SxProps<Theme> = {
  borderColor: 'rgba(255, 255, 255, 0.3)',
  color: 'rgba(255, 255, 255, 0.7)',
  '&:hover': {
    borderColor: 'rgba(255, 255, 255, 0.5)',
    backgroundColor: BG_COLORS.GLASS,
  },
};

/**
 * Outlined button matching TextField height (56px)
 */
/**
 * Высота 56 — чтобы кнопка стояла вровень с соседним `TextField` размера `medium`.
 * На телефоне пара распадается в колонку (поле над кнопкой), ровнять не с чем —
 * там кнопка живёт по общей высоте из `BUTTON_SIZES`.
 */
export const glassOutlinedButtonTall: SxProps<Theme> = {
  ...glassOutlinedButton,
  height: { xs: BUTTON_SIZES.height.xs, sm: 56 },
  minWidth: 100,
};

/**
 * Clear/Reset button style
 */
/**
 * Группа переключателей (`ToggleButtonGroup`): фильтр статуса, выбор варианта в диалоге.
 * Белый/серый — синего в интерактиве не бывает, см. docs/ui-patterns.md §6.
 */
export const toggleGroup: SxProps<Theme> = {
  '& .MuiToggleButton-root': {
    color: TEXT_COLORS.MUTED,
    borderColor: BORDER_COLORS.DEFAULT,
    fontSize: '0.8rem',
    py: 0.6,
    px: 1.5,
    textTransform: 'none',
    '&.Mui-selected': {
      color: '#fff',
      backgroundColor: 'rgba(255, 255, 255, 0.12)',
      '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.18)' },
    },
    '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.06)' },
  },
};

export const clearButton: SxProps<Theme> = {
  borderColor: 'text.secondary',
  color: 'text.secondary',
  height: '56px',
  '&:hover': {
    borderColor: 'rgba(255, 255, 255, 0.5)',
    backgroundColor: BG_COLORS.GLASS,
  },
};

// ============================================
// TAB STYLES
// ============================================

/**
 * Tabs container with white indicator
 * Replaces: sx={{ mb: 2, borderBottom: 1, borderColor: 'rgba(255, 255, 255, 0.1)', '& .MuiTabs-indicator': { backgroundColor: '#ffffff' } }}
 */
export const tabsContainer: SxProps<Theme> = {
  mb: 2,
  borderBottom: 1,
  borderColor: BORDER_COLORS.DEFAULT,
  '& .MuiTabs-indicator': {
    backgroundColor: '#ffffff',
  },
};

/**
 * Default Tab item style
 * Replaces: sx={{ color: 'rgba(255, 255, 255, 0.6)', '&.Mui-selected': { color: '#ffffff' } }}
 */
export const tabItem: SxProps<Theme> = {
  minHeight: 40,
  py: 1,
  color: 'rgba(255, 255, 255, 0.6)',
  '&.Mui-selected': { color: '#ffffff' },
  fontSize: '0.875rem',
};

/**
 * Tab item with responsive styles for mobile
 */
export const tabItemResponsive: SxProps<Theme> = {
  ...tabItem,
  '@media (max-width: 420px)': {
    fontSize: '0.75rem',
    minHeight: 32,
    py: 0.5,
    px: 1,
    minWidth: 'auto',
  },
};

// ============================================
// TABLE STYLES
// ============================================

/**
 * Table wrapper style (for clipping scrollbar at rounded corners)
 * Use this to wrap TableContainer for proper borderRadius
 * IMPORTANT: Must be used with flex container parent for scroll to work
 */
export const tableWrapper: SxProps<Theme> = {
  borderRadius: 2,
  overflow: 'hidden',
  boxShadow: 3,
  flex: 1,
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
};

/**
 * Glass TableContainer style (matching RatingPage pattern)
 * IMPORTANT: Must include flex: 1 and minHeight: 0 for scrolling
 */
export const glassTableContainer: SxProps<Theme> = {
  flex: 1,
  minHeight: 0,
  overflow: 'auto',
  // Scrollbar styles (matching dashboard tables)
  '&::-webkit-scrollbar': { width: 8, height: 8 },
  '&::-webkit-scrollbar-track': {
    background: SCROLLBAR_COLORS.TRACK,
    borderRadius: 4,
    marginTop: 7,
    marginBottom: 0,
  },
  '&::-webkit-scrollbar-thumb': {
    background: SCROLLBAR_COLORS.THUMB,
    borderRadius: 4,
  },
  '&::-webkit-scrollbar-thumb:hover': {
    background: SCROLLBAR_COLORS.THUMB_HOVER,
  },
  scrollbarWidth: 'thin',
  scrollbarColor: `${SCROLLBAR_COLORS.THUMB_SOLID} ${SCROLLBAR_COLORS.TRACK}`,
};

/**
 * Table header row style (matching RatingPage)
 */
export const tableHeader: SxProps<Theme> = {
  '& th': {
    fontWeight: 'bold',
    bgcolor: BG_COLORS.SURFACE,
  },
};

/**
 * Table header cell style (for stickyHeader tables)
 */
export const tableHeaderCell: SxProps<Theme> = {
  fontWeight: 'bold',
  bgcolor: BG_COLORS.SURFACE,
};

/**
 * Полоска через строку в таблицах. Одним значением: им же красит строки лёгкая грида игровых
 * данных (`components/game-data/cells.tsx`), где полоска считается по номеру строки.
 */
export const ROW_STRIPE_BG = 'rgba(0, 0, 0, 0.02)';

/**
 * Hoverable table row (matching RatingPage pattern)
 * Alternating colors + hover effect
 */
export const hoverableRow: SxProps<Theme> = {
  '&:nth-of-type(odd)': { bgcolor: ROW_STRIPE_BG },
  '&:hover': { bgcolor: `${BG_COLORS.GLASS_DARK} !important` },
};

/**
 * Clickable table row (hoverable + cursor pointer)
 */
export const clickableRow: SxProps<Theme> = {
  '&:nth-of-type(odd)': { bgcolor: ROW_STRIPE_BG },
  '&:hover': { bgcolor: `${BG_COLORS.GLASS_DARK} !important` },
  cursor: 'pointer',
};

/**
 * Alternating row style
 */
export const alternatingRow: SxProps<Theme> = {
  '&:nth-of-type(odd)': { bgcolor: 'rgba(0, 0, 0, 0.02)' },
  transition: 'background-color 0.2s',
  '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.05) !important' },
};

/**
 * Comprehensive responsive styles for tables (RatingPage pattern)
 * Apply to TableContainer
 */
export const tableResponsiveStyles: SxProps<Theme> = {
  '@media (max-width: 1200px)': {
    '& .MuiTableCell-root': {
      fontSize: '0.75rem',
      padding: '8px 12px',
    },
    '& .MuiTypography-body1': {
      fontSize: '0.75rem',
    },
    '& .MuiTypography-body2': {
      fontSize: '0.7rem',
    },
    '& .MuiChip-root': {
      height: '22px',
      '& .MuiChip-label': {
        fontSize: '0.7rem',
        px: 1,
      },
    },
  },
  '@media (max-width: 1100px)': {
    '& .MuiTableCell-root': {
      fontSize: '0.65rem',
      padding: '6px 8px',
    },
  },
  '@media (max-width: 550px)': {
    '& .MuiTableCell-root': {
      fontSize: '0.55rem',
      padding: '4px 6px',
    },
  },
  '@media (max-width: 440px)': {
    '& .MuiTableCell-root': {
      fontSize: '0.5rem',
      padding: '3px 4px',
    },
  },
};

/**
 * Responsive styles for Tabs component
 */
export const tabsResponsiveStyles: SxProps<Theme> = {
  '@media (max-width: 1100px)': {
    minHeight: 40,
    '& .MuiTab-root': {
      minHeight: 40,
      py: 0.5,
      px: 1,
      fontSize: '0.75rem',
      minWidth: 'auto',
    },
    '& .MuiTab-iconWrapper': {
      fontSize: '1rem',
    },
  },
  '@media (max-width: 550px)': {
    minHeight: 36,
    '& .MuiTab-root': {
      minHeight: 36,
      py: 0.5,
      px: 0.5,
      fontSize: '0.6rem',
      minWidth: 'auto',
    },
  },
  '@media (max-width: 440px)': {
    minHeight: 32,
    '& .MuiTab-root': {
      minHeight: 32,
      py: 0.25,
      px: 0.25,
      fontSize: '0.5rem',
    },
  },
};

// ============================================
// LIST ITEM STYLES
// ============================================

/**
 * Glass list item style
 * Replaces: sx={{ p: 1, mb: 1, background: 'rgba(255, 255, 255, 0.08)', borderRadius: 1, border: '1px solid rgba(255, 255, 255, 0.12)' }}
 */
export const glassListItem: SxProps<Theme> = {
  p: 1,
  mb: 1,
  background: BG_COLORS.GLASS,
  borderRadius: 1,
  border: `1px solid ${BORDER_COLORS.LIGHT}`,
};

/**
 * Glass list item - active/selected state (success variant)
 */
export const glassListItemActive: SxProps<Theme> = {
  p: 1,
  mb: 1,
  background: BG_COLORS.SUCCESS_LIGHT,
  borderRadius: 1,
  border: `1px solid ${BORDER_COLORS.SUCCESS}`,
};

// ============================================
// SLIDER STYLES
// ============================================

/**
 * White themed slider (PaymentPage pattern)
 */
export const whiteSlider: SxProps<Theme> = {
  color: 'rgba(255, 255, 255, 0.7)',
  '& .MuiSlider-thumb': {
    backgroundColor: '#ffffff',
    '&:hover, &.Mui-focusVisible, &.Mui-active': {
      boxShadow: '0 0 0 8px rgba(255, 255, 255, 0.16)',
    },
  },
  '& .MuiSlider-track': {
    background: 'rgba(255, 255, 255, 0.7)',
  },
  '& .MuiSlider-rail': {
    color: 'rgba(255, 255, 255, 0.3)',
  },
  '& .MuiSlider-mark': {
    color: 'rgba(255, 255, 255, 0.5)',
  },
  '& .MuiSlider-markLabel': {
    color: TEXT_COLORS.SECONDARY,
    fontSize: '0.75rem',
  },
};

// ============================================
// INPUT STYLES
// ============================================

/**
 * Hide number input spinners (for TextField type="number")
 * Replaces: MozAppearance: 'textfield', WebkitAppearance: 'none'
 */
export const hideNumberSpinners: SxProps<Theme> = {
  '& input[type=number]': {
    MozAppearance: 'textfield',
  },
  '& input[type=number]::-webkit-outer-spin-button': {
    WebkitAppearance: 'none',
    margin: 0,
  },
  '& input[type=number]::-webkit-inner-spin-button': {
    WebkitAppearance: 'none',
    margin: 0,
  },
};

// ============================================
// LOADING / EMPTY STATES
// ============================================

/**
 * Centered loader container
 */
export const centeredLoader: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'center',
  py: 8,
};

/**
 * Empty state container
 */
export const emptyState: SxProps<Theme> = {
  textAlign: 'center',
  py: 6,
};

// ============================================
// FLEXBOX UTILITIES
// ============================================

/**
 * Flex center (horizontal and vertical)
 */
export const flexCenter: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

/**
 * Flex space-between with vertical center
 */
export const flexBetween: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
};

/**
 * Flex column
 */
export const flexColumn: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
};

/**
 * Flex with gap 1 (8px)
 */
export const flexGap1: SxProps<Theme> = {
  display: 'flex',
  gap: 1,
};

/**
 * Flex with gap 2 (16px)
 */
export const flexGap2: SxProps<Theme> = {
  display: 'flex',
  gap: 2,
};

/**
 * Flex row with items centered
 */
export const flexRow: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
};

// ============================================
// DIALOG STYLES
// ============================================

/**
 * Dialog content section background
 */
export const dialogSection: SxProps<Theme> = {
  background: 'rgba(255, 255, 255, 0.02)',
};

/**
 * Dialog title with background
 */
export const dialogTitleBg: SxProps<Theme> = {
  color: TEXT_COLORS.LIGHT,
  background: 'rgba(255, 255, 255, 0.02)',
};

// ============================================
// TEXT STYLES
// ============================================

/**
 * Secondary text color
 */
export const textSecondary: SxProps<Theme> = {
  color: TEXT_COLORS.SECONDARY,
};

/**
 * Light text color
 */
export const textLight: SxProps<Theme> = {
  color: TEXT_COLORS.LIGHT,
};

/**
 * Muted text color
 */
export const textMuted: SxProps<Theme> = {
  color: TEXT_COLORS.MUTED,
};

// ============================================
// FILTER STYLES
// ============================================

/**
 * Filter container - flex row with gap
 */
export const filterContainer: SxProps<Theme> = {
  display: 'flex',
  gap: 1,
  alignItems: 'stretch',
  flexWrap: 'wrap',
  '@media (max-width: 600px)': {
    gap: 1,
    flexDirection: 'column',
  },
};

/**
 * Compact filter input style - for TextField and Select
 */
export const filterInputCompact: SxProps<Theme> = {
  '& .MuiInputBase-input': {
    py: 0.75,
    fontSize: { xs: '0.8rem', sm: '0.875rem' },
  },
  '& .MuiSelect-select': {
    py: 0.75,
  },
};

/**
 * Filter field - responsive minWidth
 */
export const filterField: SxProps<Theme> = {
  minWidth: 150,
  '@media (max-width: 1400px)': {
    minWidth: 130,
  },
  '@media (max-width: 600px)': {
    minWidth: '100%',
    width: '100%',
  },
};

/**
 * Filter field wide - for longer inputs
 */
export const filterFieldWide: SxProps<Theme> = {
  minWidth: 200,
  '@media (max-width: 1400px)': {
    minWidth: 170,
  },
  '@media (max-width: 600px)': {
    minWidth: '100%',
    width: '100%',
  },
};

/**
 * Поле строки фильтров в редакторах игровых данных (`/admin/game-data/**`).
 *
 * Рост задан явно: от одних только ужатых отступов `TextField` выходил 32 px против 35 px у
 * `Select` и кнопок рядом, и поле в общем ряду смотрелось мельче соседей (замер 14.09.2026).
 * Подписи (`InputLabel`) таким полям не давать: MUI поднимает её над значением, и в поле ростом
 * 35 px это читается как две строки — вместо неё приглушённый плейсхолдер (docs/game-data.md §5a).
 */
export const filterFieldCompact: SxProps<Theme> = {
  minWidth: 150,
  '@media (max-width: 1400px)': {
    minWidth: 130,
  },
  '@media (max-width: 600px)': {
    minWidth: '100%',
    width: '100%',
  },
  '& .MuiInputBase-root': {
    height: 35,
  },
  '& .MuiInputBase-input': {
    py: 0.75,
    fontSize: { xs: '0.8rem', sm: '0.875rem' },
  },
  '& .MuiSelect-select': {
    py: 0.75,
  },
};

/**
 * Combined filter field wide with compact input styling
 */
export const filterFieldWideCompact: SxProps<Theme> = {
  minWidth: 200,
  '@media (max-width: 1400px)': {
    minWidth: 170,
  },
  '@media (max-width: 600px)': {
    minWidth: '100%',
    width: '100%',
  },
  '& .MuiInputBase-input': {
    py: 0.75,
    fontSize: { xs: '0.8rem', sm: '0.875rem' },
  },
  '& .MuiSelect-select': {
    py: 0.75,
  },
};
